/**
 * Фильтры списка в URL, чтобы ссылкой можно было поделиться (KAN-743,
 * вынесено из useCatalogFilters для списка заказов KAN-746).
 *
 * update(key, value): пустое значение убирает параметр; история не растёт
 * (replace). Поле поиска живёт локально и попадает в URL с задержкой.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';

export const URL_SEARCH_DEBOUNCE_MS = 300;

export function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const update = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return { searchParams, update };
}

export interface UrlSearch {
  /** Применённый поиск (из URL). */
  search: string;
  /** Текст в поле поиска (до задержки). */
  searchInput: string;
  setSearchInput: (value: string) => void;
}

/** Поиск ?search=: поле ввода пишется в URL через URL_SEARCH_DEBOUNCE_MS. */
export function useUrlSearch(
  searchParams: URLSearchParams,
  update: (key: string, value: string | null) => void
): UrlSearch {
  const search = (searchParams.get('search') ?? '').trim();
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebounce(searchInput.trim(), URL_SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedSearch !== search) update('search', debouncedSearch || null);
    // search в зависимостях не нужен: пишем только изменение поля ввода.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, update]);

  return { search, searchInput, setSearchInput };
}
